import { config } from '../../config';
import type MapView from '@arcgis/core/views/MapView';
import { Box, useTheme } from '@presov.sk/presov-ui-react';
import React from 'react';

type Props = {
  location: [number, number];
  zoom?: number;
};

const SimpleMap: React.FC<Props> = ({ location, zoom = 13 }) => {
  const theme = useTheme();

  const mapDiv = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!mapDiv.current) return;

    let view: MapView | null = null;
    let isMounted = true;

    (async () => {
      try {
        const [GraphicModule, MapModule, MapViewModule] = await Promise.all([
          import('@arcgis/core/Graphic'),
          import('@arcgis/core/Map'),
          import('@arcgis/core/views/MapView'),
        ]);

        if (!isMounted) return;

        const Graphic = GraphicModule.default;
        const MapComponent = MapModule.default;
        const MapView = MapViewModule.default;

        const map = new MapComponent({
          basemap: config.CUSTOM_MAP_BASEMAP,
        });

        view = new MapView({
          container: mapDiv.current,
          map,
          center: location,
          zoom: zoom,
          navigation: {
            actionMap: {
              mouseWheel: 'none',
              dragPrimary: 'pan',
            },
            browserTouchPanEnabled: false,
          },
        });

        const pointGraphic = new Graphic({
          geometry: {
            type: 'point',
            longitude: location[0],
            latitude: location[1],
          },
          symbol: {
            type: 'simple-marker',
            color: theme.palette.primary.main,
            outline: { color: 'white', width: 1 },
          },
        });
        view.graphics.add(pointGraphic);

        // Button to center the map on the user's location
        const button = document.createElement('div');
        button.className = 'esri-widget esri-widget--button esri-interactive';
        button.innerHTML = '<span class="esri-icon-locate"></span>';
        button.addEventListener('click', () => {
          view.goTo({
            center: location,
            zoom: 13,
          });
        });
        view.ui.add(button, 'top-left');
      } catch (error) {
        console.error('Failed to load ArcGIS modules:', error);
      }
    })();

    return () => {
      isMounted = false;
      if (view) view.destroy();
    };
  }, [location, theme.palette.primary.main, zoom]);

  return (
    <Box
      ref={mapDiv}
      sx={{
        width: '100%',
        height: '100%',
        '--esri-view-outline': 'none !important',

        '.esri-ui-inner-container': {
          inset: `${theme.typography.pxToRem(8)} !important`,
        },

        '.esri-attribution': {
          bottom: -8,
          left: -8,
          right: -8,
          fontSize: 10,
        },
      }}
    />
  );
};

export default SimpleMap;
